import React, { useContext, useMemo, useRef, useState } from 'react';
import { DndProvider, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { EllipsisOutlined } from '@ant-design/icons';
import { Button, Dropdown, Flex, MenuProps } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import TaskActionMenu from '@totem/components/ticketing/details/tasks/taskActionMenu';
import TaskContext from '@totem/components/ticketing/details/tasks/taskContext';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { aggregateHours } from '@totem/components/ticketing/details/timeEntry/utilities';
import { ticketPermissions } from '@totem/components/ticketing/permissions';
import {
  HierarchicalTask,
  Task,
  TASK_TYPE_GROUP,
  TASK_TYPE_MILESTONE,
  TASK_TYPE_TASK,
} from '@totem/components/ticketing/ticketing';
import { formatDuration } from '@totem/components/ticketing/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import IBDIcon from '@totem/styles/Icon';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';

const filterHierarchy = (
  nodes: HierarchicalTask[],
  statuses: string[],
): HierarchicalTask[] =>
  nodes.reduce<HierarchicalTask[]>((acc, node) => {
    const filteredChildren = filterHierarchy(node.children ?? [], statuses);
    if (statuses.includes(node.status) || filteredChildren.length > 0) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, []);

const filterInternalOnly = (nodes: HierarchicalTask[]): HierarchicalTask[] =>
  nodes.reduce<HierarchicalTask[]>((acc, node) => {
    if (!node.internalOnly) {
      acc.push({ ...node, children: filterInternalOnly(node.children ?? []) });
    }
    return acc;
  }, []);

const TasksTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket } = useContext(TicketContext);
  const {
    tasks,
    taskWorkflow,
    taskHierarchy,
    onTaskAction,
    onTaskStatusChange,
    getTask,
  } = useContext(TaskContext);

  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const statusFilters = useMemo(() => {
    const unique = [
      ...new Set(
        tasks
          .filter((tsk) => tsk.type !== TASK_TYPE_GROUP && tsk.status)
          .map((tsk) => tsk.status),
      ),
    ].sort();
    return unique.map((sts) => {
      const step = taskWorkflow.find((wf) => wf.code === sts);
      return { text: step ? step.name : sts, value: sts };
    });
  }, [tasks, taskWorkflow]);

  const displayHierarchy = useMemo(() => {
    let hierarchy = isNotNull(taskHierarchy) ? taskHierarchy : [];
    if (!permissions.internal.hasPermissions(userProfile)) {
      hierarchy = filterInternalOnly(hierarchy);
    }
    if (statusFilter.length === 0) return hierarchy;
    return filterHierarchy(hierarchy, statusFilter);
  }, [taskHierarchy, statusFilter, userProfile]);

  const DND_ITEM_TYPE = 'TASK_ROW';

  type DropPosition = 'above' | 'below' | 'inside';

  type DragItem = {
    taskNumber: number;
  };

  type DragRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
    index?: number;
    record?: HierarchicalTask;
    moveTask?: (
      draggedTaskNumber: number,
      targetTaskNumber: number,
      position: DropPosition,
    ) => void;
    canDrag?: boolean;
    canDrop?: boolean;
  };

  const canEditTask = (task: Task) =>
    ticketPermissions.taskEditor.hasPermissions(userProfile, ticket, task);

  const getDropPosition = (
    monitor: DropTargetMonitor<DragItem, void>,
    ref: React.RefObject<HTMLTableRowElement>,
    allowInside: boolean,
  ): DropPosition | null => {
    const clientOffset = monitor.getClientOffset();
    const element = ref.current;
    if (!clientOffset || !element) {
      return null;
    }

    const rect = element.getBoundingClientRect();
    const hoverY = clientOffset.y - rect.top;
    const height = rect.bottom - rect.top;

    if (hoverY < height / 3) {
      return 'above';
    }
    if (hoverY > (height * 2) / 3) {
      return 'below';
    }
    if (allowInside) {
      return 'inside';
    }
    return hoverY < height / 2 ? 'above' : 'below';
  };

  const DraggableTaskRow = ({
    index,
    record,
    moveTask,
    canDrag,
    canDrop,
    className,
    style,
    ...restProps
  }: DragRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);

    const [{ isDragging }, drag] = useDrag({
      type: DND_ITEM_TYPE,
      item: { taskNumber: record ? record.number : -1 } as DragItem,
      canDrag: !!canDrag,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [{ isOver, dropPosition }, drop] = useDrop<
      DragItem,
      void,
      { isOver: boolean; dropPosition: DropPosition | null }
    >({
      accept: DND_ITEM_TYPE,
      canDrop: (item) =>
        !!canDrop &&
        isNotNull(record) &&
        item.taskNumber !== record.number &&
        record.parent !== item.taskNumber,
      drop: (item, monitor) => {
        if (!record || !moveTask) return;
        const position = getDropPosition(
          monitor,
          ref,
          record.type === TASK_TYPE_GROUP,
        );
        if (position) {
          moveTask(item.taskNumber, record.number, position);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        dropPosition:
          record && monitor.isOver({ shallow: true })
            ? getDropPosition(monitor, ref, record.type === TASK_TYPE_GROUP)
            : null,
      }),
    });

    drag(drop(ref));

    const dropStyles: React.CSSProperties = {};
    if (isOver && dropPosition === 'above') {
      dropStyles.boxShadow = 'inset 0 3px 0 0 #1890ff';

      dropStyles.backgroundColor = '#f0f5ff';
    } else if (isOver && dropPosition === 'below') {
      dropStyles.boxShadow = 'inset 0 -3px 0 0 #1890ff';

      dropStyles.backgroundColor = '#f0f5ff';
    } else if (isOver && dropPosition === 'inside') {
      dropStyles.boxShadow = 'inset 0 0 0 3px #1890ff';

      dropStyles.backgroundColor = '#f0f5ff';
    }

    return (
      <tr
        {...restProps}
        ref={ref}
        className={className}
        style={{
          cursor: canDrag ? 'grab' : 'default',
          opacity: isDragging ? 0.3 : 1,
          ...dropStyles,
          ...style,
        }}
        data-row-index={index}
      />
    );
  };

  const handleTaskDrop = (
    draggedTaskNumber: number,
    targetTaskNumber: number,
    position: DropPosition,
  ) => {
    const draggedTask = getTask(draggedTaskNumber);
    const targetTask = getTask(targetTaskNumber);

    if (!isNotNull(draggedTask) || !isNotNull(targetTask)) {
      return;
    }

    if (!canEditTask(draggedTask) || draggedTask.id === targetTask.id) {
      return;
    }

    const extras = {
      draggedTaskNumber,
      targetTaskNumber,
      position,
    };

    onTaskAction('move-task-dnd', JSON.stringify(extras));
  };

  const getResourceById = (resourceId: string) => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.partiesInvolved)
    ) {
      for (const partyInvolved of ticket.ticket.partiesInvolved) {
        if (partyInvolved.id === resourceId) {
          return partyInvolved.name;
        }
      }
    }
    return '';
  };

  const getResources = (resourceIds: string[]) => {
    if (resourceIds === null) {
      return <div />;
    }
    return (
      <div>
        {resourceIds.map((id: string) => (
          <div key={id}>{getResourceById(id)}</div>
        ))}
      </div>
    );
  };

  const getStatusChangeMenuOptions = (tsk: Task) => {
    let menuItems: MenuProps['items'] = [];

    const currentStatus = taskWorkflow.find((chk) => chk.code === tsk.status);
    if (isNotNull(currentStatus)) {
      menuItems = currentStatus.options.map((itm) => {
        return {
          key: `key:status_update:${itm}`,
          label: itm,
          title: itm,
        };
      });
    }

    return menuItems;
  };

  const onMenuClicked = (key: string, record: Task) => {
    if (key.startsWith('key:status_update:')) {
      const newStatus = key.replace('key:status_update:', '');

      onTaskStatusChange(newStatus, record);
    }
  };

  const canChangeStatus = (record: Task) => {
    const isCorrectType = record.type === TASK_TYPE_TASK;
    const isNotBlocked = record.status !== 'BLOCKED';
    const hasPermissions = ticketPermissions.taskStatusChanger.hasPermissions(
      userProfile,
      ticket,
      record,
      null,
    );

    return isCorrectType && isNotBlocked && hasPermissions;
  };

  const isInternalUser = permissions.internal.hasPermissions(userProfile);

  const columns: ColumnProps<Task>[] = [
    {
      title: 'ID',
      dataIndex: 'number',
      key: 'number',
      defaultSortOrder: 'ascend',
      render: (_, record: Task) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() =>
            onTaskAction('show-task-details', JSON.stringify(record))
          }
        >
          <IBDIcon
            name={record.type}
            style={{
              marginRight: '16px',
              color: record.internalOnly
                ? colors.event.criticality.background.critical
                : colors.event.criticality.background.ok,
            }}
          />
          {record.number}
        </span>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record: Task) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() =>
            onTaskAction('show-task-details', JSON.stringify(record))
          }
        >
          {record.name}
        </span>
      ),
    },
    {
      title: 'Scheduled Start Date',
      dataIndex: 'expectedStartDate',
      key: 'expectedStartDate',
      render: (_, record: Task) => formatDateOnly(record.expectedStartDate),
    },
    ...(isInternalUser
      ? [
          {
            title: 'Expected Duration',
            dataIndex: 'expectedDuration',
            key: 'expectedDuration',
            render: (_: unknown, record: Task) =>
              formatDuration(record.expectedDuration),
          } as ColumnProps<Task>,
        ]
      : []),
    {
      title: 'Actual Start Date',
      dataIndex: 'actualStartDate',
      key: 'actualStartDate',
      render: (_, record: Task) => formatDateOnly(record.actualStartDate),
    },
    {
      title: 'Actual End Date',
      dataIndex: 'actualEndDate',
      key: 'actualEndDate',
      render: (_, record: Task) => formatDateOnly(record.actualEndDate),
    },
    {
      title: 'Resources',
      dataIndex: 'assignedResources',
      key: 'assignedResources',
      render: (_, record: Task) => getResources(record.assignedResources),
    },
    ...(isInternalUser
      ? [
          {
            title: 'Budgeted Hours',
            align: 'right',
            dataIndex: 'budgetedHours',
            key: 'budgetedHours',
            render: (_: unknown, record: Task) => {
              switch (record.type) {
                case TASK_TYPE_TASK:
                  return <Hours amount={record.budgetedHours} />;
                case TASK_TYPE_GROUP:
                  return (
                    <b>
                      <Hours amount={aggregateHours(record).budgetedHours} />
                    </b>
                  );
                default:
                  return <span />;
              }
            },
          } as ColumnProps<Task>,
          {
            title: 'Actual Hours',
            align: 'right',
            dataIndex: 'actualHours',
            key: 'actualHours',
            render: (_: unknown, record: Task) => {
              switch (record.type) {
                case TASK_TYPE_TASK:
                  return <Hours amount={record.actualHours} />;
                case TASK_TYPE_GROUP:
                  return (
                    <b>
                      <Hours amount={aggregateHours(record).actualHours} />
                    </b>
                  );
                default:
                  return <span />;
              }
            },
          } as ColumnProps<Task>,
        ]
      : []),
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filterMultiple: true,
      filteredValue: statusFilter.length > 0 ? statusFilter : null,
      filters: statusFilters,
      render: (_, record: Task) => (
        <Flex justify="flex-start" align="center" gap={16}>
          <div>
            {canChangeStatus(record) && (
              <IBTooltip content={ticketPermissions.taskStatusChanger.tooltip}>
                <Dropdown
                  placement="bottomLeft"
                  menu={{
                    items: getStatusChangeMenuOptions(record),
                    onClick: ({ key }) => onMenuClicked(key, record),
                  }}
                  trigger={['click']}
                >
                  <Button shape="circle" icon={<EllipsisOutlined />} />
                </Dropdown>
              </IBTooltip>
            )}
            {record.type === TASK_TYPE_MILESTONE && (
              <IBDIcon
                name={record.type}
                style={{ marginLeft: '12px', marginRight: '6px' }}
              />
            )}
          </div>
          <div>
            {record.type === TASK_TYPE_GROUP ? (
              ''
            ) : record.status === 'BLOCKED' ? (
              <span style={{ color: '#D22B2B' }}>{record.status}</span>
            ) : (
              record.status
            )}
          </div>
        </Flex>
      ),
    },
    ...(isInternalUser
      ? [
          {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            render: (_: unknown, record: Task) => (
              <TaskActionMenu task={record} />
            ),
          } as ColumnProps<Task>,
        ]
      : []),
  ];

  return (
    <DndProvider backend={HTML5Backend}>
      <IBTable<HierarchicalTask>
        showSorterTooltip
        columns={columns}
        dataSource={displayHierarchy}
        onChange={(_, filters) =>
          setStatusFilter((filters.status as string[]) || [])
        }
        loading={false}
        rowKey="id"
        expandable={{
          defaultExpandAllRows: true,
          expandIcon: ({ expanded, onExpand, record }) => {
            // Check if the current row actually has children
            if (isNotNull(record.children) && record.children.length > 0) {
              // Render the default Antd expand icon (or a custom one)
              return (
                <span
                  onClick={(evt) => onExpand(record, evt)}
                  style={{ cursor: 'pointer', marginRight: 8 }}
                >
                  {/* Ant Design uses a specific icon for tree expansion (usually the arrow) */}
                  {expanded ? '▼' : '▶'}
                </span>
              );
            }
            // If the row has no children, return null to hide the icon
            return <span style={{ marginRight: 8 }}>&nbsp;</span>;
          },
        }}
        components={{
          body: {
            row: DraggableTaskRow,
          },
        }}
        onRow={(record, index) =>
          ({
            index,
            record,
            moveTask: handleTaskDrop,
            canDrag: canEditTask(record),
            canDrop: canEditTask(record),
          }) as React.HTMLAttributes<HTMLElement>
        }
      />
    </DndProvider>
  );
};

export default TasksTable;
